import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Layers } from "lucide-react";

interface AddMaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId: string;
}

export function AddMaterialDialog({ open, onOpenChange, batchId }: AddMaterialDialogProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    material_inventory_id: "",
    material_name: "",
    material_lot_number: "",
    quantity_used: "",
    unit: "",
    supplier: "",
    expiry_date: "",
    notes: "",
  });

  const { data: materials } = useQuery({
    queryKey: ["material-inventory-select"],
    queryFn: async () => {
      const { data, error } = await api
        .from("material_inventory")
        .select("id, name, code, unit, supplier")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  const handleMaterialSelect = (materialId: string) => {
    const material = materials?.find((m) => m.id === materialId);
    if (material) {
      setFormData({
        ...formData,
        material_inventory_id: materialId,
        material_name: material.name,
        unit: material.unit || "",
        supplier: material.supplier || "",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.material_name || !formData.quantity_used) {
      toast.error("Completa los campos requeridos");
      return;
    }

    setLoading(true);
    try {
      const user = getCurrentUser();

      // Insert material
      const { error: matError } = await api.from("batch_materials").insert({
        batch_id: batchId,
        material_inventory_id: formData.material_inventory_id || null,
        material_name: formData.material_name,
        material_lot_number: formData.material_lot_number || null,
        quantity_used: parseFloat(formData.quantity_used),
        unit: formData.unit || null,
        supplier: formData.supplier || null,
        expiry_date: formData.expiry_date || null,
        added_by: user?.id,
        notes: formData.notes || null,
      });

      if (matError) throw matError;

      // Create traceability event
      await api.from("traceability_events").insert({
        batch_id: batchId,
        event_type: "material_added",
        description: `Material agregado: ${formData.material_name} (${formData.quantity_used} ${formData.unit})`,
        performed_by: user?.id,
        metadata: {
          material_name: formData.material_name,
          material_lot: formData.material_lot_number,
          quantity: formData.quantity_used,
          unit: formData.unit,
        },
      });

      toast.success("Material agregado correctamente");
      queryClient.invalidateQueries({ queryKey: ["batch-materials", batchId] });
      queryClient.invalidateQueries({ queryKey: ["traceability-events", batchId] });

      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Error al agregar material");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      material_inventory_id: "",
      material_name: "",
      material_lot_number: "",
      quantity_used: "",
      unit: "",
      supplier: "",
      expiry_date: "",
      notes: "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Agregar Material al Lote
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Material del Inventario</Label>
            <Select
              value={formData.material_inventory_id}
              onValueChange={handleMaterialSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar material..." />
              </SelectTrigger>
              <SelectContent>
                {materials?.map((mat) => (
                  <SelectItem key={mat.id} value={mat.id}>
                    {mat.code ? `${mat.code} - ` : ""}{mat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              O ingresa los datos manualmente abajo
            </p>
          </div>

          <div className="space-y-2">
            <Label>Nombre del Material *</Label>
            <Input
              value={formData.material_name}
              onChange={(e) => setFormData({ ...formData, material_name: e.target.value })}
              placeholder="Ej: Resina Epoxi"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cantidad Usada *</Label>
              <Input
                type="number"
                step="0.001"
                value={formData.quantity_used}
                onChange={(e) => setFormData({ ...formData, quantity_used: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Unidad</Label>
              <Input
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="kg, L, m, etc"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Numero de Lote del Material</Label>
            <Input
              value={formData.material_lot_number}
              onChange={(e) => setFormData({ ...formData, material_lot_number: e.target.value })}
              placeholder="Ej: MAT-2024-001"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Proveedor</Label>
              <Input
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                placeholder="Nombre proveedor"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Caducidad</Label>
              <Input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Observaciones adicionales..."
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Agregar Material"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
